import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { API, BUDGET_OPTIONS, INTEREST_OPTIONS } from "../lib/site";

const inputCls =
  "h-12 w-full border border-navy-900/15 bg-white px-4 text-sm text-navy-900 placeholder:text-gray-400 outline-none transition-[border-color,box-shadow] duration-300 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/25";

const selectCls = inputCls + " appearance-none cursor-pointer";

export const LeadForm = ({
  tid = "lead-form",
  source = "website",
  project = "",
  defaultInterest = "",
  intent = "",
  dark = false,
  showMessage = true,
  submitLabel = "Request Callback",
  budgetOptions = BUDGET_OPTIONS,
  budgetLabel = "Preferred Budget",
  onSuccess,
}) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", interest: defaultInterest, budget: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source, project });
      toast.success("Thank you! Our property expert will call you within 30 minutes.", {
        description: "Your enquiry has been received by Estate Paradise.",
      });
      setForm({ name: "", phone: "", email: "", interest: defaultInterest, budget: "", message: "" });
      onSuccess?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  const labelCls = `mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] ${dark ? "text-white/60" : "text-navy-900/60"}`;

  return (
    <form onSubmit={submit} className="space-y-5" data-testid={tid}>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor={`${tid}-name`}>Full Name</label>
          <input id={`${tid}-name`} data-testid={`${tid}-name-input`} required minLength={2} value={form.name} onChange={set("name")} placeholder="Rahul Sharma" className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor={`${tid}-phone`}>Phone Number</label>
          <input id={`${tid}-phone`} data-testid={`${tid}-phone-input`} required minLength={8} value={form.phone} onChange={set("phone")} placeholder="+91 98XXXXXXXX" className={inputCls} />
        </div>
      </div>
      <div>
        <label className={labelCls} htmlFor={`${tid}-email`}>Email Address</label>
        <input id={`${tid}-email`} data-testid={`${tid}-email-input`} required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor={`${tid}-interest`}>Interested Category</label>
          <select id={`${tid}-interest`} data-testid={`${tid}-interest-select`} required value={form.interest} onChange={set("interest")} className={selectCls}>
            <option value="" disabled>Select category</option>
            {INTEREST_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor={`${tid}-budget`}>{budgetLabel}</label>
          <select id={`${tid}-budget`} data-testid={`${tid}-budget-select`} required value={form.budget} onChange={set("budget")} className={selectCls}>
            <option value="" disabled>Select budget</option>
            {budgetOptions.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
      </div>
      {showMessage && (
        <div>
          <label className={labelCls} htmlFor={`${tid}-message`}>Message (Optional)</label>
          <textarea id={`${tid}-message`} data-testid={`${tid}-message-input`} rows={3} value={form.message} onChange={set("message")} placeholder="Preferred location, configuration, timeline…" className={`${inputCls} h-auto py-3`} />
        </div>
      )}
      <button
        type="submit"
        disabled={loading}
        data-testid={`${tid}-submit-btn`}
        className="group flex w-full items-center justify-center gap-2 bg-gold-500 px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-navy-900 shadow-[0_10px_24px_rgb(197,160,89,0.35)] transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-0.5 hover:bg-gold-400 hover:shadow-[0_16px_32px_rgb(197,160,89,0.45)] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
        {loading ? "Sending…" : submitLabel}
      </button>
    </form>
  );
};
